import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import { RiChat3Line, RiRepeatLine, RiCloseCircleLine, RiPencilLine, RiRefreshLine, RiTrophyLine, RiErrorWarningLine, RiPhoneLine } from "@remixicon/react";
import PageHeader from "../common/PageHeader";
import Loader from "../common/Loader";
import StatusPill from "../common/StatusPill";
import ConfirmModal from "../common/ConfirmModal";
import { getOrderDetails, getTopThreeBids, awardBid, cancelBidRequest, repeatOrder, updateBidRequestStatus, increaseOrderQty } from "../../api/bids";
import { STATUS_INFO } from "../../config";
import { orderNumber, formatGallons, formatMoney, formatDeliveryWindow } from "../../utils/status";

// ordr-flutter buyer_order_details.dart isOrderEditable(): quantity can't change once
// the order is delivered, completed, cancelled or pending cancellation.
const LOCKED_STATES = [6, 7, 19, 21, 22, 34];
const CANCELLED_STATES = [21, 22, 34];

export default function OrderDetail() {
	const { orderId } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [order, setOrder] = useState(null);
	const [bids, setBids] = useState([]);
	const [bidsLoading, setBidsLoading] = useState(false);
	const [showCancel, setShowCancel] = useState(false);
	const [showEditQty, setShowEditQty] = useState(false);
	const [awarding, setAwarding] = useState(null);
	const [confirmRepeat, setConfirmRepeat] = useState(false);

	const load = async () => {
		setLoading(true);
		const res = await getOrderDetails(orderId);
		if (res && !res.error) setOrder(res.data);
		else toast.error(res?.msg || "Could not load order");
		setLoading(false);
	};

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderId]);

	const loadBids = async () => {
		setBidsLoading(true);
		const res = await getTopThreeBids(orderId);
		if (res && !res.error) setBids(res.data || []);
		setBidsLoading(false);
	};

	useEffect(() => {
		if (order) loadBids();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [order?.status]);

	const handleAward = async () => {
		const res = await awardBid({ bidConfirmationId: awarding.id });
		setAwarding(null);
		if (res && !res.error) {
			toast.success("Bid awarded!");
			load();
		} else {
			toast.error(res?.msg || "Could not award bid");
		}
	};

	const handleRepeat = async () => {
		setConfirmRepeat(false);
		const res = await repeatOrder({ bidRequestId: orderId });
		if (res && !res.error) {
			// Same "Bid Created" -> "Buyer Approved" transition required after a plain
			// createBidRequest — see NewBidRequest.jsx's confirmSubmit for why.
			const approveRes = await updateBidRequestStatus(res.data?.bidRequestId, { bidRequestState: 2 });
			if (approveRes && !approveRes.error) {
				toast.success("Repeat order created");
				navigate("/orders", { state: { tab: "bidRequest" } });
			} else {
				toast.error(approveRes?.msg || "Order was created but could not be sent to sellers. Please contact support.");
			}
		} else {
			toast.error(res?.msg || "Could not repeat order");
		}
	};

	if (loading) return <Loader />;
	if (!order) return <PageHeader title="Order not found" back="/orders" />;

	const state = Number(order.bidRequestState);
	const canEditQty = !LOCKED_STATES.includes(state);
	const confirmedPrice = order.sellerBidPriceDetail?.sellerBidPriceDetail;
	const statusInfo = Array.isArray(order.statusInfo) ? order.statusInfo : [];
	const cancellation = statusInfo.find((s) => Number(s.bidRequestState) === 20)?.bidCancellationReason?.[0];
	// New orders are seeded with placeholder steps (isUpdated: false) whose statusDate is the
	// backend schema default — a stale server-start time. Only reached, visible steps are real.
	const trackSteps = statusInfo
		.filter((s) => s.isUpdated && Number(s.isVisibleOnUi) !== 0)
		.sort((a, b) => new Date(a.statusDate || 0) - new Date(b.statusDate || 0));
	const loc = order.locationDetails;

	return (
		<div>
			<PageHeader
				back="/orders"
				title={`Order ${orderNumber(order, orderId)}`}
				subtitle={
					<span className="d-inline-flex align-items-center gap-2">
						<StatusPill order={order} />
						{order.createdAt ? <span>Requested {moment(order.createdAt).format("MMM D, YYYY")}</span> : null}
					</span>
				}
				actions={
					<>
						{order.sellerUserId && (
							<button className="btn-outline" onClick={() => navigate(`/messages?userId=${order.sellerUserId}&bidRequestId=${orderId}`)}>
								<RiChat3Line size={16} /> Message seller
							</button>
						)}
						<button className="btn-outline" onClick={() => setConfirmRepeat(true)}>
							<RiRepeatLine size={16} /> Repeat order
						</button>
						{!CANCELLED_STATES.includes(state) && ![19, 20].includes(state) && (
							<button className="btn-outline text-danger" onClick={() => setShowCancel(true)}>
								<RiCloseCircleLine size={16} /> Cancel
							</button>
						)}
					</>
				}
			/>

			{cancellation && [19, 20].includes(state) && (
				<div className="notice warning mb-3">
					<RiErrorWarningLine size={20} />
					<div>
						<div className="fw-bold">Cancellation requested: {cancellation.reason}</div>
						{cancellation.notes ? <div className="small">{cancellation.notes}</div> : null}
					</div>
				</div>
			)}
			{state === 2 && bids.length === 0 && (
				<div className="notice info mb-3">
					<RiTrophyLine size={20} />
					<div>
						<div className="fw-bold">Your request is live</div>
						<div className="small">Sellers are reviewing it now. The best bids will show up here — you choose which one to award.</div>
					</div>
				</div>
			)}

			<div className="row g-4">
				<div className="col-xl-7">
					<div className="card p-4 mb-4">
						<h6 className="section-title mb-2">Order details</h6>
						<Row label="Location" value={loc?.locationName} />
						<Row label="Address" value={loc ? `${loc.streetAddress}, ${loc.city}, ${loc.state} ${loc.zip}` : "-"} />
						<Row label="Product" value={order.productType?.[0]?.Title} />
						<Row
							label="Quantity"
							value={
								<span className="d-inline-flex align-items-center gap-2">
									{formatGallons(order.orderSize)}
									{canEditQty && (
										<button className="icon-inline" onClick={() => setShowEditQty(true)} title="Change quantity">
											<RiPencilLine size={15} />
										</button>
									)}
								</span>
							}
						/>
						<Row label="Delivery date" value={order.deliveryDate ? moment(order.deliveryDate).format("MMM D, YYYY") : "-"} />
						<Row label="Delivery window" value={formatDeliveryWindow(order.deliveryTime)} />
						{order.otherNotes ? <Row label="Notes" value={order.otherNotes} /> : null}
					</div>

					{order.driverInfo && Object.keys(order.driverInfo).length > 0 && (
						<div className="card p-4 mb-4">
							<h6 className="section-title mb-2">Driver</h6>
							<Row label="Name" value={order.driverInfo.firstName ? `${order.driverInfo.firstName} ${order.driverInfo.lastName || ""}` : "-"} />
							<Row
								label="Phone"
								value={
									order.driverInfo.mobileNumber ? (
										<a href={`tel:${order.driverInfo.mobileNumber}`} className="d-inline-flex align-items-center gap-1">
											<RiPhoneLine size={14} /> {order.driverInfo.mobileNumber}
										</a>
									) : (
										"-"
									)
								}
							/>
						</div>
					)}

					{trackSteps.length > 0 && (
						<div className="card p-4">
							<h6 className="section-title mb-3">Track order</h6>
							<ol className="timeline">
								{trackSteps
									.map((s, i, arr) => (
										<li key={i} className={i === arr.length - 1 ? "current" : ""}>
											<span className="timeline-dot" />
											<div>
												<div className="fw-semibold">{STATUS_INFO[s.bidRequestState] || s.bidStatus || "Update"}</div>
												{s.note ? <div className="small text-muted">{s.note}</div> : null}
												{s.statusDate ? <div className="small text-muted">{moment(s.statusDate).format("MMM D, YYYY · h:mm A")}</div> : null}
											</div>
										</li>
									))}
							</ol>
						</div>
					)}
				</div>

				<div className="col-xl-5">
					{confirmedPrice ? (
						<div className="card p-4">
							<h6 className="section-title mb-2">Confirmed price</h6>
							<PriceBreakdown price={confirmedPrice} />
						</div>
					) : (
						<div className="card p-4">
							<div className="d-flex justify-content-between align-items-center mb-3">
								<div>
									<h6 className="section-title">Top bids</h6>
									<div className="text-muted small">Pick the offer that works best for you</div>
								</div>
								<button className="btn-soft" onClick={loadBids} title="Refresh bids">
									<RiRefreshLine size={16} />
								</button>
							</div>
							{bidsLoading ? (
								<Loader small />
							) : bids.length === 0 ? (
								<div className="text-muted small py-3 text-center">No bids yet. We'll notify you as soon as sellers respond.</div>
							) : (
								<div className="d-flex flex-column gap-3">
									{bids.map((bid, idx) => {
										const price = bid.sellerBidPriceDetail || bid;
										return (
											<div className={`bid-card${idx === 0 ? " best" : ""}`} key={bid._id || idx}>
												<div className="d-flex justify-content-between align-items-start mb-2">
													<div>
														{idx === 0 ? <span className="best-tag">Best price</span> : null}
														<div className="fw-bold">{bid.sellerInfo?.companyName || bid.sellerInfo?.firstName || `Seller ${idx + 1}`}</div>
														<div className="text-muted small">{price.bidPricePerGallon ? `${formatMoney(price.bidPricePerGallon)} / gal` : ""}</div>
													</div>
													<div className="fs-5 fw-bold">{formatMoney(price.totalPrice)}</div>
												</div>
												<PriceBreakdown price={price} compact />
												<button className="btn-accent-solid w-100 mt-3" onClick={() => setAwarding({ id: bid._id || bid.bidConfirmationId, total: price.totalPrice })}>
													Award this bid
												</button>
											</div>
										);
									})}
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			{showCancel && (
				<CancelModal
					orderId={orderId}
					currentStatus={order.status}
					onClose={() => setShowCancel(false)}
					onDone={() => {
						setShowCancel(false);
						load();
					}}
				/>
			)}
			{showEditQty && (
				<EditQtyModal
					orderId={order.id || orderId}
					current={order.orderSize}
					onClose={() => setShowEditQty(false)}
					onDone={(orderSize) => {
						setShowEditQty(false);
						setOrder((o) => ({ ...o, orderSize }));
					}}
				/>
			)}
			{awarding && (
				<ConfirmModal
					title="Award this bid?"
					message={`This confirms your order with the selected seller for ${formatMoney(awarding.total)}.`}
					confirmLabel="Award bid"
					onConfirm={handleAward}
					onClose={() => setAwarding(null)}
				/>
			)}
			{confirmRepeat && (
				<ConfirmModal
					title="Repeat this order?"
					message="A new bid request with the same location, product and quantity will be sent to sellers."
					confirmLabel="Create repeat order"
					onConfirm={handleRepeat}
					onClose={() => setConfirmRepeat(false)}
				/>
			)}
		</div>
	);
}

function Row({ label, value }) {
	return (
		<div className="kv-row">
			<span className="kv-label">{label}</span>
			<span className="kv-value">{value || "-"}</span>
		</div>
	);
}

function PriceBreakdown({ price = {}, compact }) {
	return (
		<div className={compact ? "small" : ""}>
			<Row label="Price per gallon" value={formatMoney(price.bidPricePerGallon)} />
			<Row label="Quantity" value={formatGallons(price.quantity)} />
			<Row label="Product total" value={formatMoney(price.wholePriceOfQuantity)} />
			<Row label="Freight charges" value={formatMoney(price.freightCharges)} />
			<Row label="Total price" value={formatMoney(price.totalPrice)} />
		</div>
	);
}

// Mirrors ordr-flutter editOrderPopUp -> EditOrderSizeEvent (v1/buyer/order/increaseQty)
function EditQtyModal({ orderId, current, onClose, onDone }) {
	const [value, setValue] = useState(String(current || ""));
	const [saving, setSaving] = useState(false);
	const qty = Number(String(value).replace(/,/g, ""));

	const save = async () => {
		if (!qty || qty <= 0) {
			toast.error("Enter a quantity greater than 0");
			return;
		}
		setSaving(true);
		const res = await increaseOrderQty({ bidId: orderId, orderQty: String(qty) });
		setSaving(false);
		if (res && !res.error) {
			toast.success("Quantity updated");
			onDone(qty);
		} else {
			toast.error(res?.msg || "Could not update quantity");
		}
	};

	return (
		<Modal title="Change quantity" onClose={onClose}>
			<label className="form-label">Quantity (gallons)</label>
			<input className="form-control mb-4" inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
			<div className="d-flex gap-2 justify-content-end">
				<button className="btn-outline" onClick={onClose}>
					Cancel
				</button>
				<button className="btn-accent-solid" disabled={saving} onClick={save}>
					{saving ? "Saving..." : "Save quantity"}
				</button>
			</div>
		</Modal>
	);
}

const CANCEL_REASONS = ["Changed my mind", "Found a better price", "Delivery timing conflict", "Other"];

function CancelModal({ orderId, currentStatus, onClose, onDone }) {
	const [reason, setReason] = useState(CANCEL_REASONS[0]);
	const [notes, setNotes] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const submit = async () => {
		setSubmitting(true);
		const res = await cancelBidRequest(orderId, { reason, notes, currentBidState: currentStatus });
		setSubmitting(false);
		if (res && !res.error) {
			toast.success("Cancellation requested");
			onDone();
		} else {
			toast.error(res?.msg || "Could not submit cancellation");
		}
	};

	return (
		<Modal title="Cancel order" onClose={onClose}>
			<div className="mb-3">
				<label className="form-label">Reason</label>
				<select className="form-select" value={reason} onChange={(e) => setReason(e.target.value)}>
					{CANCEL_REASONS.map((r) => (
						<option key={r} value={r}>
							{r}
						</option>
					))}
				</select>
			</div>
			<div className="mb-4">
				<label className="form-label">Additional notes</label>
				<textarea className="form-control" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
			</div>
			<div className="d-flex gap-2 justify-content-end">
				<button className="btn-outline" onClick={onClose}>
					Keep order
				</button>
				<button className="btn btn-danger" disabled={submitting} onClick={submit}>
					{submitting ? "Submitting..." : "Request cancellation"}
				</button>
			</div>
		</Modal>
	);
}

function Modal({ title, children, onClose }) {
	return (
		<div className="modal d-block" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
			<div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
				<div className="modal-content p-4">
					<h5 className="fw-bold mb-3">{title}</h5>
					{children}
				</div>
			</div>
		</div>
	);
}
